import { axiosClient } from '@/libs/axios-client';

/**
 * Custom document builder API client. Templates live in the `DocumentTemplate`
 * collection (new), separate from the legacy per-company DOCX upload Map.
 */

/** Static token palette for builder templates. */
export const getDocumentTemplateTokens = async () => {
  const response = await axiosClient.get('/documents/document-templates/tokens');
  return response.data;
};

/**
 * Lists a company's document templates (active only unless includeArchived).
 * Pass `audience: 'employee'` to get only templates that are about an employee
 * (used by the per-employee generate flows); omit it to get all audiences.
 */
export const listDocumentTemplates = async (
  companyId,
  includeArchived = false,
  audience = null
) => {
  const response = await axiosClient.get('/documents/document-templates', {
    params: {
      company: companyId,
      includeArchived,
      ...(audience ? { audience } : {}),
    },
  });
  return response.data;
};

/** Fetches a single template by id. */
export const getDocumentTemplate = async (id) => {
  const response = await axiosClient.get(`/documents/document-templates/${id}`);
  return response.data;
};

/** Creates a new builder template. */
export const createDocumentTemplate = async (payload) => {
  const response = await axiosClient.post(
    '/documents/document-templates',
    payload
  );
  return response.data;
};

/** Updates an existing template. */
export const updateDocumentTemplate = async (id, payload) => {
  const response = await axiosClient.put(
    `/documents/document-templates/${id}`,
    payload
  );
  return response.data;
};

/** Archives (soft-deletes) a template. */
export const archiveDocumentTemplate = async (id) => {
  const response = await axiosClient.delete(
    `/documents/document-templates/${id}`
  );
  return response.data;
};

/**
 * Uploads a DOCX file as an `upload`-source template (multipart). The DOCX is
 * stored on S3 and rendered in-flight (docxtemplater → Adobe PDF) on download.
 */
export const uploadDocumentTemplate = async (
  companyId,
  {
    name,
    category,
    audience,
    autoGenerateOnLeaveType,
    replacesDefaultDocument,
    scope,
    allowedCompanies,
    customFields,
    contractKind,
    emailDelivery,
  } = {},
  file
) => {
  const formData = new FormData();
  formData.append('template', file);
  formData.append('company', companyId);
  formData.append('name', name);
  if (category) formData.append('category', category);
  if (audience) formData.append('audience', audience);
  if (autoGenerateOnLeaveType)
    formData.append('autoGenerateOnLeaveType', autoGenerateOnLeaveType);
  if (replacesDefaultDocument != null)
    formData.append('replacesDefaultDocument', String(Boolean(replacesDefaultDocument)));
  if (scope) formData.append('scope', scope);
  if (allowedCompanies)
    formData.append('allowedCompanies', JSON.stringify(allowedCompanies));
  if (customFields) formData.append('customFields', JSON.stringify(customFields));
  if (contractKind) formData.append('contractKind', contractKind);
  if (emailDelivery) formData.append('emailDelivery', emailDelivery);
  const response = await axiosClient.post(
    '/documents/document-templates/upload',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
};

/**
 * Downloads the source DOCX of an `upload` template so the client can edit it in
 * Word. Triggers a browser download of the returned blob.
 */
export const downloadDocumentTemplateFile = async (id, filename = 'template.docx') => {
  const response = await axiosClient.get(
    `/documents/document-templates/${id}/file`,
    { responseType: 'blob' }
  );
  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Replaces the source DOCX of an `upload` template with a newly edited file.
 */
export const replaceDocumentTemplateFile = async (id, file) => {
  const formData = new FormData();
  formData.append('template', file);
  const response = await axiosClient.put(
    `/documents/document-templates/${id}/file`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
};

/**
 * Generates a custom document for one employee. Persists the chosen template +
 * ad-hoc field values as a CUSTOM Document and returns its id; the caller then
 * downloads it via the existing /documents/download endpoint.
 */
export const generateCustomDocument = async (payload) => {
  const response = await axiosClient.post('/documents/custom', payload);
  return response.data;
};

/**
 * Fills a template for one employee as a draft to review before the document
 * is created. Returns `{ draftId, source, contentHtml }` for editor templates,
 * or `{ draftId, source, editor: { documentServerUrl, config } }` for Word
 * templates (opened in ONLYOFFICE).
 */
export const createDocumentDraft = async (payload) => {
  const response = await axiosClient.post('/documents/custom/drafts', payload);
  return response.data;
};

/**
 * Creates the document from a reviewed draft. `contentHtml` is the edited body
 * of an editor template; Word drafts are saved by the editor itself. `email`
 * (`{ send, toEmployee, toHr, extra }`) emails the created document as a PDF.
 * Returns `{ documentId, attachment, contract, email }`.
 */
export const finalizeDocumentDraft = async (draftId, { contentHtml, email } = {}) => {
  const response = await axiosClient.post(
    `/documents/custom/drafts/${draftId}/finalize`,
    { ...(contentHtml !== undefined ? { contentHtml } : {}), ...(email ? { email } : {}) }
  );
  return response.data;
};

/**
 * Opens an uploaded Word template in the editor with the placeholder palette
 * (`palette`: `{ hint, groups: [{ title, custom?, tokens: [{ key, label }] }] }`).
 * Returns `{ draftId, editor: { documentServerUrl, config } }`.
 */
export const openTemplateEditSession = async (templateId, palette) => {
  const response = await axiosClient.post(
    `/documents/document-templates/${templateId}/edit-session`,
    { palette }
  );
  return response.data;
};

/**
 * Saves the Word template edited in the editor over its file. Returns the
 * template, with `missingFieldKeys` for fields that still have no placeholder.
 */
export const saveTemplateEditSession = async (draftId) => {
  const response = await axiosClient.post(
    `/documents/document-templates/edit-sessions/${draftId}/save`
  );
  return response.data;
};

/** Abandons a draft. */
export const cancelDocumentDraft = async (draftId) => {
  const response = await axiosClient.delete(`/documents/custom/drafts/${draftId}`);
  return response.data;
};

/**
 * Bulk generation: one template + shared ad-hoc values for many employees.
 * Returns `{ created: [{employeeId, documentId}], failed: [{employeeId, message}] }`.
 */
export const generateCustomDocumentBulk = async (payload) => {
  const response = await axiosClient.post('/documents/custom/bulk', payload);
  return response.data;
};

/**
 * Generates a company-level document (internal/external business documentation)
 * from a template — not tied to any employee. Returns the created document id.
 */
export const generateCompanyDocument = async (payload) => {
  const response = await axiosClient.post('/documents/company', payload);
  return response.data;
};

/**
 * Lists a company's generated (non-employee) custom documents, server-paginated.
 * Returns `{ items, count }`. Optional `search` (title), `dateFrom`/`dateTo`
 * (ISO), and `offset`/`limit` for pagination.
 */
export const listCompanyDocuments = async (companyId, params = {}) => {
  const response = await axiosClient.get('/documents/company', {
    params: { company: companyId, ...params },
  });
  return response.data;
};

const DocumentTemplatesService = {
  getDocumentTemplateTokens,
  listDocumentTemplates,
  getDocumentTemplate,
  createDocumentTemplate,
  uploadDocumentTemplate,
  downloadDocumentTemplateFile,
  replaceDocumentTemplateFile,
  updateDocumentTemplate,
  archiveDocumentTemplate,
  generateCustomDocument,
  createDocumentDraft,
  finalizeDocumentDraft,
  cancelDocumentDraft,
  openTemplateEditSession,
  saveTemplateEditSession,
  generateCustomDocumentBulk,
  generateCompanyDocument,
  listCompanyDocuments,
};

export default DocumentTemplatesService;
